import { useEffect, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AddingNewElement } from '../components/AddNewElementRow';

export type AddElementType = 'character' | 'scene' | 'prop';

const VALID: readonly AddElementType[] = ['character', 'scene', 'prop'];

export function useAddElementTarget(): AddElementType | null {
  const location = useLocation();
  return useMemo<AddElementType | null>(() => {
    const value = new URLSearchParams(location.search).get('addElement');
    return value && (VALID as readonly string[]).includes(value) ? (value as AddElementType) : null;
  }, [location.search]);
}

export interface ConsumeAddElementHandlers {
  setAnalysisTab: (tab: AddElementType) => void;
  setAddingNewChar: Dispatch<SetStateAction<AddingNewElement | null>>;
  setAddingNewScene: Dispatch<SetStateAction<AddingNewElement | null>>;
  setAddingNewProp: Dispatch<SetStateAction<AddingNewElement | null>>;
}

export function useConsumeAddElementTarget(
  target: AddElementType | null,
  handlers: ConsumeAddElementHandlers,
): void {
  const location = useLocation();
  const navigate = useNavigate();
  const { setAnalysisTab, setAddingNewChar, setAddingNewScene, setAddingNewProp } = handlers;

  useEffect(() => {
    if (!target) return;
    // 1. 显式切到目标标签（初始 state 只在挂载跑一次，覆盖不到同实例客户端再导航）
    setAnalysisTab(target);
    // 2. 清三个新增弹窗状态，防止其它 tab 残留
    setAddingNewChar(null);
    setAddingNewScene(null);
    setAddingNewProp(null);
    // 3. 以空表单打开目标弹窗
    const openByType = {
      character: setAddingNewChar,
      scene: setAddingNewScene,
      prop: setAddingNewProp,
    } as const;
    openByType[target]({ name: '', desc: '', episodes: [], loading: false });
    // 4. 仅剥除 addElement，保留其它 query
    const sp = new URLSearchParams(location.search);
    sp.delete('addElement');
    navigate(
      { pathname: location.pathname, search: sp.toString() ? `?${sp.toString()}` : '' },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
}
